<!-- SiteView.vue -->
<script lang="ts">
import { ref, defineComponent, computed, onMounted, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSites } from '@/composables/useSites'
// import type { File } from '@/composables/useFiles';
import { useFiles } from '@/composables/useFiles'
import MapComponent from '@/components/MapComponent.vue'
import { useMapStore } from '@/stores/mapStore'
import { fromLonLat } from 'ol/proj'
// import { Extent } from 'ol/extent'
import Polygon from 'ol/geom/Polygon.js'
import Feature from 'ol/Feature.js'
import VectorSource from 'ol/source/Vector.js'
import VectorLayer from 'ol/layer/Vector.js'
import Style from 'ol/style/Style.js'
import Fill from 'ol/style/Fill.js'
import Stroke from 'ol/style/Stroke.js'
import FileListVue from '@/components/FileList.vue'
import { toLonLat } from 'ol/proj'
import SiteTools from '@/components/SiteTools.vue'
import { Divider } from 'primevue'
import { useViewportHeight } from '@/composables/useViewportHeight'


export default defineComponent({
  components: {
    MapComponent,
    FileListVue,
    SiteTools
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { viewportHeight } = useViewportHeight()
    const siteId = computed(() => route.params.site_id) as ComputedRef<string>
    // Make sure siteId is a string not a string[]

    const { sites, fetchItems } = useSites()
    const siteInfo = computed(() => {
      return sites.value.find((site) => site.name === siteId.value)
    })

    const mapStore = useMapStore()

    const mapHeight = computed(() => {
      return `${Math.floor(window.innerWidth / 3)}px`
    })

    const tools = ref([
    {
        id: 1,
        name: 'Structural Health Monitoring',
        description: 'Precise radar measurements of structural deformation and displacement, for long term monitoring of critical infrastructure.',
        link: 'psi/shm',
        img: new URL('@/assets/shm.png', import.meta.url).href
    },
    {
        id: 2,
        name: 'Coastal Defence Monitoring',
        description: 'Measurements of slope instability, coastal erosion, and deformation of coastal defences.',
        link: 'psi/coastal',
        img: new URL('@/assets/cdm.png', import.meta.url).href
    },
    // {
    //     id: 3,
    //     name: 'Change Detection and Alerts',
    //     description: 'Activity in remote sensing data requiring attention, such as significant deformation or unexpected changes in observations which could indicate a problem.',
    //     link: 'alerts',
    //     img: new URL('@/assets/cda.png', import.meta.url).href
    // },
    {
        id: 4,
        name: 'Configure Site',
        description: 'Configure the site for monitoring, including setting up monitoring points and defining the area of interest.',
        link: 'config',
        img: new URL('@/assets/homehero_white.jpg', import.meta.url).href
    },
    {
        id: 5,
        name: 'Data Search',
        description: 'Search for data from a variety of sources, including satellite data, weather data, and other sources.',
        link: 'data_search',
        img: new URL('@/assets/bluemarble.jpg', import.meta.url).href
    },
    {
        id: 6,
        name: 'Data Viewer',
        description: 'View intermediate data products and results from processing campaigns.',
        link: 'data',
        img: new URL('@/assets/ifg.png', import.meta.url).href
    }
    ])

    onMounted(async () => {
      await fetchItems()

      const mapContainer = mapStore.map

      if (!mapContainer) {
        throw new Error('Map not initialized')
      }
      const mapContainerView = mapContainer.getView()

      const setZoom = () => {
        mapContainerView.setZoom(10)
      }

      const setCenter = () => {
        if (siteInfo.value) {
          mapContainerView.setCenter(
            fromLonLat([siteInfo.value.centre_lon, siteInfo.value.centre_lat])
          )
        }
      }

      if (!siteInfo.value) {
        throw new Error('Site not found')
      }
      console.log('siteInfo:', siteInfo.value)
      // Create a polygon feature for the site (square around geometry)
      // Geometry is [north, east, south, west] string, first split by comma, then map to float
      const geomString = siteInfo.value.geometry
      // remove []
      const siteGeometry = geomString
        .slice(1, geomString.length - 1)
        .split(',')
        .map((coord) => parseFloat(coord))
      console.log('siteGeometry:', siteGeometry)
      const siteExtent = [
        [siteGeometry[3], siteGeometry[0]],
        [siteGeometry[3], siteGeometry[2]],
        [siteGeometry[1], siteGeometry[2]],
        [siteGeometry[1], siteGeometry[0]],
        [siteGeometry[3], siteGeometry[0]]
      ].map((coord) => fromLonLat(coord))
      const sitePolygon = new Polygon([siteExtent])
      const polyStyle = new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.0)'
        }),
        stroke: new Stroke({
          color: '#dddddd',
          width: 3,
          lineDash: [3, 1],
          lineCap: 'butt'
        })
      })

      // Zoom to the site extent

      const siteBounds = sitePolygon.getExtent()
      // const mapSize = mapContainer.getSize()
      // if (!mapSize) {
      //   throw new Error('Map size not initialized')
      // }
      mapContainerView.fit(siteBounds, { size: mapContainer.getSize() })
      console.log('Zoom level:', mapContainerView.getZoom())
      console.log('Should be fitting to site bounds:', siteBounds)
      // Convert extent to Coordinates
      const sbCoords = [
        [siteBounds[0], siteBounds[1]],
        [siteBounds[2], siteBounds[3]]
      ]
      //  In lat lon that is:
      const siteBoundsLonLat = sbCoords.map((coord) => toLonLat(coord))

      console.log('Should be fitting to site bounds:', siteBoundsLonLat)
      // Zoom in a bit more
      // mapContainerView.setZoom(mapContainerView.getZoom() + 1)

      const siteFeature = new Feature(sitePolygon)
      // Create a vector source and layer
      const vectorSource = new VectorSource({
        features: [siteFeature]
      })
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: polyStyle
      })
      // Add the vector layer to the map
      mapContainer.addLayer(vectorLayer)

      setZoom()
      setCenter()

      mapContainerView.fit(sitePolygon, { padding: [100, 100, 100, 100] })

      // listen for file-click event emitted by FileList component
    })

    const goBackToSitesOverview = () => {
      router.push('/sites')
    }

    return {
      siteId,
      siteInfo,
      sites,
      tools,
      mapHeight,
      goBackToSitesOverview,
      handleClick: (tool: any) => {
        console.log('Tool clicked:', tool)
        const url = `/site/${siteId.value}/${tool.link}`
        router.push(`/site/${siteId.value}/${tool.link}`)
        console.log('Navigating to:', `${url}`)
      }
    }
  }
})
</script>

<template>
    <div class="flex h-screen">
      <Card class="w-1over3 flex flex-col">
        <template #content>
          <div class="flex items-center gap-3 mb-2">
          <Button
            @click="goBackToSitesOverview"
            icon="pi pi-arrow-left"
            severity="secondary"
            outlined
            size="small"
            class="flex-shrink-0"
            aria-label="Back to Site"
          ></Button>
          <h1>{{ siteId }} Dashboard</h1>
          </div>
          <p>{{ siteInfo?.description }}</p>
          <Divider />
          <div id="site-info">{{ siteInfo ? '' : 'Loading...' }}</div>
          <MapComponent :fixedHeight="mapHeight" />
          
        </template>
      </Card>
      <div class="w-2over3">
        <ScrollPanel
                class="flex-grow"
                :dt="{
                    bar: {
                        background: '{primary.color}'
                    }
                }"
            >
                <div class="grid grid-cols-2 gap-4 p-4">
                    <template v-for="tool in tools">
                        <Card 
                            @click="handleClick(tool)" 
                            class="h-full"
                            style="cursor: pointer;" 
                            :clickable="true"
                        >
                            <template #header>
                                <img :src="tool.img" alt="Tool image" style="width: 100%; height: 100px; object-fit: cover;" />
                            </template>
                            <template #content>
                                <h2>{{ tool.name }}</h2>
                                <p>{{ tool.description }}</p>
                            </template>
                        </Card>
                    </template>
                </div>
            </ScrollPanel>
      </div>
    </div>
  </template>
  
  <style scoped>
  .w-1over3 {
    flex-grow: 1;
    height: 100%;
  }
  .w-2over3 {
    flex-grow: 2;
    height: 100%;
  }

  :deep(.p-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  :deep(.p-card-content) {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  :deep(.p-scrollpanel) {
    height: 100%;
  }

  :deep(.p-scrollpanel-content) {
    height: 100%;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  </style>